import { ReservationStatusType, reservationStatusValues } from '@/models/reservation-reservation-status'
import { ApprovedStatusType, approvedStatusValues } from '@/models/reservation-approved-status'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { TabType } from '@/components/pages/accommodation-management/list'
import qs from 'qs'

export type EntireSearchContentType = {
  name: string
  nameKana: string
  searchReservationId: string
  roomNumber: string
  checkinDateRange: { from: string; to: string } | undefined
  checkoutDateRange: { from: string; to: string } | undefined
  stayDateRange: { from: string; to: string } | undefined
  reserveDateRange: { from: string; to: string } | undefined
  selectGuestRoomAssign: 'all' | 'assigned' | 'notAssigned'
  approvedStatuses: ApprovedStatusType[]
  reservationStatuses: ReservationStatusType[]
  channelCodeName: string | undefined
  guestRoomTypeId: string | undefined
  guestRoomTypeName: string | undefined
  telephone: string
  note: string
}

export const EntireSearchContentInitial = (opt?: {
  tabType?: TabType
  override?: Partial<EntireSearchContentType>
  isDisabledParams?: boolean
}): EntireSearchContentType => {
  const params = opt?.isDisabledParams ? {} : qs.parse(window.location.search.substring(1))
  delete params?.refresh
  const initialConditions: EntireSearchContentType = {
    name: '',
    nameKana: '',
    searchReservationId: '',
    roomNumber: '',
    checkinDateRange: undefined,
    checkoutDateRange: undefined,
    stayDateRange: undefined,
    reserveDateRange: undefined,
    selectGuestRoomAssign: 'all',
    telephone: '',
    note: '',
    channelCodeName: 'all',
    guestRoomTypeId: 'all',
    guestRoomTypeName: '',
    approvedStatuses: [ApprovedStatusType.Reservation, ApprovedStatusType.Checkin, ApprovedStatusType.Stay, ApprovedStatusType.Checkout],
    reservationStatuses: [ReservationStatusType.NewReport, ReservationStatusType.ModificationReport],
    ...opt?.override,
    ...params,
  }
  switch (opt?.tabType) {
    case 'reservation':
      return {
        ...initialConditions,
        approvedStatuses: [ApprovedStatusType.Reservation],
        reservationStatuses: initialConditions.reservationStatuses.map(s => Number(s) as ReservationStatusType),
      }
    case 'checkin':
      return { ...initialConditions, approvedStatuses: [ApprovedStatusType.Checkin] }
    case 'stay':
      return { ...initialConditions, approvedStatuses: [ApprovedStatusType.Stay] }
    case 'checkout':
      return { ...initialConditions, approvedStatuses: [ApprovedStatusType.Checkout] }
    default:
      return {
        ...initialConditions,
        reservationStatuses: initialConditions.reservationStatuses.map(s => Number(s) as ReservationStatusType),
        approvedStatuses: initialConditions.approvedStatuses.map(s => Number(s) as ApprovedStatusType),
      }
  }
}

export const convertLabelWithEntireSearch = (tabType?: TabType) => {
  const condition = qs.parse(window.location.search.substring(1))
  const formatDateRange = (value: { from: string; to: string } | undefined): string | undefined =>
    value ? `${dayjs(value.from).format(t('YYYY-MM-DD'))}~${dayjs(value.to).format(t('YYYY-MM-DD'))}` : undefined

  const formatRoomAssignmentStatus = (s: string | undefined): string | undefined => {
    if (!s || s === 'all') {
      return undefined
    }
    return s === 'assigned' ? t('Assigned') : t('Not assigned')
  }

  let eachTabProps = {}
  switch (tabType) {
    case 'reservation':
      eachTabProps = {
        [t('Check-in date')]: formatDateRange(condition.checkinDateRange),
        [t('Status')]: condition.reservationStatuses?.map(s => reservationStatusValues[s]).join('・'),
      }
      break
    case 'checkin':
      eachTabProps = {
        [t('Date of Check-in')]: formatDateRange(condition.checkinDateRange),
      }
      break
    case 'stay':
      break
    case 'checkout':
      break
    default:
      eachTabProps = {
        [t('Check-in date')]: formatDateRange(condition.checkinDateRange),
        [t('Accommodation management status')]: condition.approvedStatuses?.map(s => approvedStatusValues[s]).join('・'),
        [t('Status')]: condition.reservationStatuses?.map(s => reservationStatusValues[s]).join('・'),
      }
  }

  return {
    [t('Full name')]: condition.name,
    [t('Furigana')]: condition.nameKana,
    [t('Reservation ID')]: condition.searchReservationId,
    [t('Room number')]: condition.roomNumber,
    [t('Check-out date')]: formatDateRange(condition.checkoutDateRange),
    [t('Date of stay')]: formatDateRange(condition.stayDateRange),
    [t('Reservation date')]: formatDateRange(condition.reserveDateRange),
    [t('Room assignment status')]: formatRoomAssignmentStatus(condition.selectGuestRoomAssign),
    [t('Channel')]: !condition.channelCodeName || condition.channelCodeName === 'all' ? undefined : condition.channelCodeName,
    [t('Room type')]: !condition.guestRoomTypeName || condition.guestRoomTypeName === 'all' ? undefined : condition.guestRoomTypeName,
    [t('Phone number')]: condition.telephone,
    [t('ReservationNote')]: condition.note,
    ...eachTabProps,
  }
}
